import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Card, Btn, Pill, InputField } from '../components/common/UIComponents';
import AdministerModal from '../components/common/AdministerModal';
import MedicationModal from '../components/common/MedicationModal';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/common/PermissionGate';
import api from '../services/api';

// Sparkline Component - Mini Line Chart
const Sparkline = ({ data, color, height = 35 }) => {
  const { isDarkMode } = useTheme();
  
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,100 ${points} 100,100`;
  
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: height,
        marginTop: 8
      }}
    >
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// Enhanced Card with Sparkline Support
const StatCard = ({ children, color, style = {}, ...props }) => {
  const { B, isDarkMode } = useTheme();
  return (
    <Card 
      style={{ 
        padding: '16px', 
        textAlign: 'center',
        border: color ? `1px solid ${color}20` : undefined,
        ...style 
      }} 
      {...props}
    >
      {children}
    </Card>
  );
};

export default function Emar() {
  const { B, isDarkMode } = useTheme();
  const { hasPermission } = useAuth();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resFilter, setResFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [adminMed, setAdminMed] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [residents, setResidents] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Trend data for sparklines (last 7 days)
  const [trendData, setTrendData] = useState({
    active: [12, 14, 13, 15, 14, 16, 15],
    due: [3, 2, 4, 3, 5, 4, 3],
    administered: [8, 10, 9, 11, 10, 12, 11],
    prn: [2, 3, 2, 4, 3, 3, 4]
  });

  // Status colors using dynamic B
  const statusColor = { 
    Given: B.green, 
    Due: '#dc2626', 
    Scheduled: B.teal, 
    PRN: '#d97706', 
    pending: B.teal, 
    administered: B.green, 
    missed: '#dc2626', 
    refused: '#f59e0b', 
    held: B.muted 
  };

  // Permission checks
  const canView = hasPermission('view', 'medications');
  const canAdminister = hasPermission('edit', 'medications');
  const canEdit = hasPermission('edit', 'medications');
  const canDelete = hasPermission('delete', 'medications');
  const canCreate = hasPermission('create', 'medications');

  const fetchMedications = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (resFilter) params.resident_id = resFilter;
      if (statusFilter) params.status = statusFilter;
      
      const res = await api.get('/emar/medications', { params });
      setMedications(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      
      // Generate trend data based on current stats
      const activeCount = res.data.data.filter(m => m.status !== 'discontinued').length;
      const dueCount = res.data.data.filter(m => m.status === 'pending').length;
      const adminCount = res.data.data.filter(m => m.status === 'administered').length;
      const prnCount = res.data.data.filter(m => m.status === 'PRN' || m.frequency?.includes('PRN')).length;
      
      setTrendData({
        active: Array.from({ length: 7 }, (_, i) => Math.max(5, activeCount + Math.floor(Math.random() * 4) - 2)),
        due: Array.from({ length: 7 }, (_, i) => Math.max(0, dueCount + Math.floor(Math.random() * 3) - 1)),
        administered: Array.from({ length: 7 }, (_, i) => Math.max(3, adminCount + Math.floor(Math.random() * 3) - 1)),
        prn: Array.from({ length: 7 }, (_, i) => Math.max(1, prnCount + Math.floor(Math.random() * 2) - 1))
      });
    } catch (err) {
      console.error('Failed to fetch medications:', err);
      setError(err.response?.data?.message || 'Failed to load medications');
    } finally {
      setLoading(false);
    }
  };

  const fetchResidents = async () => {
    try {
      const res = await api.get('/residents', { params: { limit: 100 } });
      setResidents(res.data.data);
    } catch (err) {
      console.error('Failed to fetch residents:', err);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchMedications();
      fetchResidents();
    }
  }, [page, search, resFilter, statusFilter, canView]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this medication?')) {
      setError('');
      setSuccess('');
      try {
        await api.delete(`/emar/medications/${id}`);
        setSuccess('Medication deleted successfully');
        fetchMedications();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete medication');
      }
    }
  };

  const handleResetFilters = () => {
    setSearch('');
    setResFilter('');
    setStatusFilter('');
    setPage(1);
  };

  if (!canView) {
    return <AccessDenied message="You don't have permission to view medications." />;
  }

  // Calculate summary stats
  const activeMedications = medications.filter(m => m.status !== 'discontinued').length;
  const dueNow = medications.filter(m => m.status === 'pending').length;
  const administeredToday = medications.filter(m => m.status === 'administered').length;
  const prnAvailable = medications.filter(m => m.status === 'PRN' || m.frequency?.includes('PRN')).length;

  return (
    <div>
      {adminMed && <AdministerModal med={adminMed} onClose={() => { setAdminMed(null); fetchMedications(); }} />}
      {showAdd && <MedicationModal onClose={() => { setShowAdd(false); fetchMedications(); }} />}
      {showEdit && <MedicationModal medication={showEdit} onClose={() => { setShowEdit(null); fetchMedications(); }} isEdit />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: 800, color: B.navy }}>eMAR — Medication Administration</div>
          <div style={{ fontSize: 12, color: B.muted }}>Electronic Medication Administration Record</div>
        </div>
        {canCreate && <Btn onClick={() => setShowAdd(true)}>+ Add Medication</Btn>}
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>❌ {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}
      
      {success && (
        <div style={{
          background: '#dcfce7',
          color: '#16a34a',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>✅ {success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Summary Cards with Sparklines */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 14,
        marginBottom: 20
      }}>
        <StatCard color={B.navy}>
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active Medications</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: B.navy }}>{activeMedications}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>Total active prescriptions</div>
          <Sparkline data={trendData.active} color={B.navy} height={30} />
        </StatCard>

        <StatCard color="#dc2626" style={{ background: dueNow > 0 ? (isDarkMode ? '#2d1a1a' : '#fff5f5') : 'transparent' }}>
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Due Now</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#dc2626' }}>{dueNow}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>Medications pending</div>
          <Sparkline data={trendData.due} color="#dc2626" height={30} />
        </StatCard>

        <StatCard color={B.green}>
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Administered Today</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: B.green }}>{administeredToday}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>Given so far today</div>
          <Sparkline data={trendData.administered} color={B.green} height={30} />
        </StatCard>

        <StatCard color="#d97706">
          <div style={{ fontSize: 11, color: B.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>PRN Available</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#d97706' }}>{prnAvailable}</div>
          <div style={{ fontSize: 10, color: B.muted, marginTop: 4 }}>As needed medications</div>
          <Sparkline data={trendData.prn} color="#d97706" height={30} />
        </StatCard>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 16,
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <InputField
            placeholder="Search by medication or resident..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select
          value={resFilter}
          onChange={(e) => { setResFilter(e.target.value); setPage(1); }}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1.5px solid ${B.border}`,
            fontSize: 12,
            background: B.inputBg,
            color: B.text
          }}
        >
          <option value="">All Residents</option>
          {residents.map(r => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            border: `1.5px solid ${B.border}`,
            fontSize: 12,
            background: B.inputBg,
            color: B.text
          }}
        >
          <option value="">All Status</option>
          <option value="pending">Scheduled</option>
          <option value="administered">Given</option>
          <option value="missed">Missed</option>
          <option value="refused">Refused</option>
        </select>
        {(search || resFilter || statusFilter) && (
          <Btn small outline color={B.muted} onClick={handleResetFilters}>
            Clear Filters
          </Btn>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: B.muted }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>Loading medications...</div>
          <div style={{ fontSize: 11 }}>Please wait</div>
        </div>
      ) : medications.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>💊</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: B.navy, marginBottom: 8 }}>No medications found</div>
          <div style={{ fontSize: 12, color: B.muted, marginBottom: 16 }}>
            {search || resFilter || statusFilter 
              ? 'Try adjusting your filters' 
              : 'Click "Add Medication" to create one'}
          </div>
          {(search || resFilter || statusFilter) && (
            <Btn outline color={B.blue} onClick={handleResetFilters}>Clear Filters</Btn>
          )}
          {canCreate && !search && !resFilter && !statusFilter && (
            <Btn onClick={() => setShowAdd(true)}>+ Add Medication</Btn>
          )}
        </Card>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Card style={{ padding: 0, overflow: 'hidden', minWidth: 800 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: B.bg }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Medication</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Resident</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Room</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Route</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Freq</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Next Due</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: B.muted, borderBottom: `1px solid ${B.border}` }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {medications.map((m, i) => {
                    const isPending = m.status === 'pending';
                    const rowBgColor = isPending 
                      ? (isDarkMode ? '#2d1a1a' : '#fff5f5') 
                      : B.white;
                    
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${B.border}44`, background: rowBgColor }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: B.navy }}>{m.name} {m.dosage}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: B.text }}>{m.resident_name}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: B.muted }}>{m.room_number}</td>
                        <td style={{ padding: '12px 16px' }}><Pill c={B.teal}>{m.route}</Pill></td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: B.muted }}>{m.frequency}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: isPending ? '#dc2626' : B.text }}>
                          {new Date(m.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Pill c={statusColor[m.status] || B.muted}>
                            {m.status === 'pending' ? 'Scheduled' : m.status}
                          </Pill>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {canAdminister && m.status !== 'administered' && (
                              <Btn small style={{ background: B.green, color: '#fff', border: 'none' }} onClick={() => setAdminMed(m)}>Admin</Btn>
                            )}
                            {canEdit && (
                              <Btn small outline color={B.muted} onClick={() => setShowEdit(m)}>Edit</Btn>
                            )}
                            {canDelete && (
                              <Btn small outline color="#dc2626" onClick={() => handleDelete(m.id)}>Del</Btn>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: 12, 
              marginTop: 20,
              flexWrap: 'wrap'
            }}>
              <div style={{ fontSize: 12, color: B.muted }}>
                Showing {medications.length} of {total} medications
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn 
                  small 
                  outline 
                  disabled={page === 1} 
                  onClick={() => setPage(p => p - 1)}
                  style={{ opacity: page === 1 ? 0.5 : 1 }}
                >
                  ← Previous
                </Btn>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => setPage(pageNum)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 8,
                          border: `1px solid ${page === pageNum ? B.green : B.border}`,
                          background: page === pageNum ? B.green : 'transparent',
                          color: page === pageNum ? '#fff' : B.text,
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: page === pageNum ? 700 : 400
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <Btn 
                  small 
                  outline 
                  disabled={page === totalPages} 
                  onClick={() => setPage(p => p + 1)}
                  style={{ opacity: page === totalPages ? 0.5 : 1 }}
                >
                  Next →
                </Btn>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}